import { useEffect, useState } from 'react';

/**
 * Tracks a CSS media query from JavaScript.
 *
 * <p>Layout alone would be cheaper, and is used where it can be. This exists for the one thing CSS
 * can't do: decide whether a component is <em>mounted</em> at all. The map pulls in Leaflet, which
 * is the largest dependency the site has, so on a phone — where the map lives behind a tab — it
 * must not be imported until someone asks for it. Hiding it with `display: none` would still load
 * every byte.
 *
 * <p>Returns false when `matchMedia` is missing rather than throwing, so a non-browser environment
 * gets the narrow layout: the safe direction, since it renders less rather than more.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);

    // Re-read on subscribe: the viewport can change between the initial render and this effect,
    // and the listener only reports changes after it is attached.
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * The one breakpoint that changes behaviour rather than only appearance: above it the timetable
 * and map sit side by side, below it they become tabs. Kept here so the value can't drift apart
 * from the matching CSS.
 */
export const WIDE_VIEWPORT = '(min-width: 62rem)';
